<!-- Your app's shell. THIS FILE IS YOURS — the factory never replaces it.
     Everything imported from ./core is maintained by `appfactory upgrade`.

     This file is now DELIBERATELY THIN. It is the root: an IonApp, the outlet
     the router paints into, and the handful of things that must exist on every
     screen at once — the demo banner, onboarding, toasts, live regions.

     The screens themselves live in src/pages/, wired up in src/router.js, and
     the state they share is src/store.js. That split is the whole reason the
     chassis moved to Ionic's router: pages that mount and unmount get real
     push/pop transitions and a per-tab back stack, which a single component
     switching on a `current` string cannot have.

     WHERE TO START:
       src/router.js      your tabs, and what each one opens
       src/pages/         one file per screen — replace the worked example
       src/store.js       the data those screens read and write
       src/styles/app.css your own CSS, loaded last and never overwritten

     The worked example runs as-is. REPLACE THE CONTENT AND KEEP THE DENSITY:
     whatever is in pages/ is what the next app looks like. -->
<template>
  <IonApp>
    <!-- Says, permanently, that this is not your data.
         Outside the router outlet on purpose, so it shows on every screen
         rather than only the one that happened to render it. The chassis used
         to put this inside its own AppShell — the Ionic port dropped that
         component, the banner silently stopped rendering, and the demo no
         longer said it was a demo. That is the one thing core/demo.js insists
         on: sample data that looks like your own is a support ticket. -->
    <DemoBanner v-if="isDemo" />

    <IonRouterOutlet />

    <!-- Three cards from IDEATION.md "First run", each over real photography
         (the factory Onboarding takes an `img` per card; app.css turns its art
         box into a full-bleed photo, see "Onboarding over photography"). Shown once, ever — gated on
         `settings.onboarded` (see DESIGN.md "Onboarding gate" for why this
         also watches Onboarding.vue's own seen-flag, since that component is
         factory-owned and has no event to tell us when it finishes). -->
    <Onboarding :cards="onboarding" finish-label="Walk the shelves" />

    <!-- The browser's walkable shop (WebXR AR, or the 3D walk-around where the
         browser has no AR). Loaded only when opened (src/lib/see-in-room.js). -->
    <WebShop v-if="webShop" :key="webShop.id" :state="webShop" @close="closeWebShop" />

    <!-- Toasts, the undo bar, and the screen-reader live regions.
         Mounted once, here. Nothing imports it to use it. -->
    <Overlays />
  </IonApp>
</template>

<script setup>
import { onMounted, onBeforeUnmount, computed, defineAsyncComponent } from 'vue';
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { Onboarding, DemoBanner, Overlays, isDemo } from './core/ui';
import { settings, refreshCatalogue } from './store.js';
import { prefs } from './core/storage.js';
import { webShop, closeWebShop } from './lib/web-shop-state.js';

const WebShop = defineAsyncComponent(() => import('./components/WebShop.vue'));

const cards = [
  {
    img: '/wall/hero-wall-960.webp',
    title: 'The whole wall, in your pocket.',
    body: 'Every coffee BeanHub sells, racked by roaster like the shop wall.',
  },
  {
    img: '/onboarding/bay-close.webp',
    title: 'Pick one up. Read the label.',
    body: 'Tap a bag to lift it off the shelf and turn it over.',
  },
  {
    img: '/wall/shopping-bag.webp',
    title: 'Fill your bag, check out with BeanHub.',
    body: 'Your bag stays on this phone, and checkout happens on beanhub.com.au.',
  },
];

// Empty once onboarded, so a component that renders nothing for an empty
// array (see Onboarding.vue) simply stops appearing — the source of truth
// the spec names is `settings.onboarded`, not the component's own storage key.
const onboarding = computed(() => (settings.onboarded ? [] : cards));

// `Onboarding.vue` is factory-owned and tracks "seen" itself under
// `onboarding.v1` in localStorage, with no event out. A short poll after
// mount copies that into `settings.onboarded` once the person finishes —
// see DESIGN.md. Cheap and self-cancelling: it stops the moment either flag
// is true.
let pollTimer = 0;
onMounted(() => {
  refreshCatalogue();
  if (!settings.onboarded) {
    pollTimer = setInterval(() => {
      if (prefs['onboarding.v1'] || settings.onboarded) {
        settings.onboarded = true;
        clearInterval(pollTimer);
      }
    }, 400);
  }
});
onBeforeUnmount(() => clearInterval(pollTimer));
</script>

